import React, { useState, useEffect, useCallback } from 'react';
import { UtensilsCrossed, Calendar, Clock, Users, ChevronLeft, ChevronRight, Check, Plus, Minus, MapPin, Phone, Mail } from 'lucide-react';

const API = 'https://qbdp-backend-production.up.railway.app/api';

const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const JOURS_FR = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];
const HEURES_MIDI = ['12:00','12:30','13:00','13:30','14:00','14:30'];
const HEURES_SOIR = ['19:00','19:30','20:00','20:30','21:00','21:30','22:00'];
const CATEGORIES_FR = { entree: 'Entrées', plat: 'Plats', dessert: 'Desserts', boisson: 'Boissons' };

function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <div style={{ width: 36, height: 36, border: '3px solid #e5e7eb', borderTop: '3px solid #1a56db', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function ReservationPublique({ slug }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [erreur, setErreur]     = useState(null);
  const [etape, setEtape]       = useState(1); // 1=infos 2=menu 3=confirmation
  const [annee, setAnnee]       = useState(new Date().getFullYear());
  const [mois, setMois]         = useState(new Date().getMonth());
  const [dateChoisie, setDateChoisie] = useState(null);
  const [service, setService]   = useState('midi');
  const [heure, setHeure]       = useState('');
  const [couverts, setCouverts] = useState(2);
  const [form, setForm]         = useState({ nom: '', email: '', telephone: '', notes: '' });
  const [commandes, setCommandes] = useState({});
  const [envoi, setEnvoi]       = useState(false);
  const [succes, setSucces]     = useState(false);

  useEffect(() => {
    fetch(`${API}/restaurant/public/${slug}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setErreur(d.error); }
        else { setData(d); }
        setLoading(false);
      })
      .catch(() => { setErreur('Impossible de charger le restaurant'); setLoading(false); });
  }, [slug]);

  const heures = service === 'midi' ? HEURES_MIDI : HEURES_SOIR;

  const getDaysInMonth = useCallback(() => {
    const firstDay = new Date(annee, mois, 1).getDay();
    const daysInMonth = new Date(annee, mois + 1, 0).getDate();
    return { firstDay, daysInMonth };
  }, [annee, mois]);

  const isPassee = (jour) => {
    const d = new Date(annee, mois, jour);
    d.setHours(0, 0, 0, 0);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return d < today;
  };

  const navMois = (delta) => {
    let nm = mois + delta;
    let ny = annee;
    if (nm < 0) { nm = 11; ny--; }
    if (nm > 11) { nm = 0; ny++; }
    setMois(nm); setAnnee(ny); setDateChoisie(null);
  };

  const changeCmd = (platId, delta) => {
    setCommandes(c => {
      const cur = c[platId] || 0;
      const next = Math.max(0, cur + delta);
      if (next === 0) { const nc = { ...c }; delete nc[platId]; return nc; }
      return { ...c, [platId]: next };
    });
  };

  const totalPlats = Object.values(commandes).reduce((s, q) => s + q, 0);

  const submit = async () => {
    if (!form.nom || !dateChoisie || !heure) return;
    setEnvoi(true);
    const cmdList = Object.entries(commandes).map(([plat_id, quantite]) => ({ plat_id: parseInt(plat_id), quantite }));
    const r = await fetch(`${API}/restaurant/public/${slug}/reserver`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form,
        date: `${annee}-${String(mois + 1).padStart(2,'0')}-${String(dateChoisie).padStart(2,'0')}`,
        heure,
        couverts,
        service,
        commandes: cmdList,
      }),
    });
    const d = await r.json();
    setEnvoi(false);
    if (d.ok) setSucces(true);
    else alert(d.error || 'Erreur lors de la réservation');
  };

  if (loading) return <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner /></div>;

  if (erreur) return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 16, padding: 40, maxWidth: 400, textAlign: 'center' }}>
        <UtensilsCrossed size={40} color="#e5e7eb" style={{ marginBottom: 16 }} />
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#374151', margin: '0 0 8px' }}>Restaurant introuvable</h2>
        <p style={{ fontSize: 14, color: '#9ca3af' }}>{erreur}</p>
      </div>
    </div>
  );

  const { restaurant, menus } = data;

  if (succes) return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 20, padding: '48px 40px', maxWidth: 440, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.06)' }}>
        <div style={{ width: 64, height: 64, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Check size={28} color="#16a34a" />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 10px' }}>Réservation confirmée !</h2>
        <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
          Votre table est réservée pour {couverts} personne{couverts > 1 ? 's' : ''} le {dateChoisie}/{mois + 1}/{annee} à {heure}.
          {totalPlats > 0 && ` Votre pré-commande de ${totalPlats} plat(s) a bien été enregistrée.`}
        </p>
        {restaurant.email && (
          <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 12 }}>Un récapitulatif vous sera envoyé à {form.email || 'votre adresse e-mail'}.</p>
        )}
        <button onClick={() => { setSucces(false); setEtape(1); setDateChoisie(null); setCommandes({}); setForm({ nom: '', email: '', telephone: '', notes: '' }); }}
          style={{ marginTop: 24, background: '#1a56db', color: 'white', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Faire une autre réservation
        </button>
      </div>
    </div>
  );

  const { firstDay, daysInMonth } = getDaysInMonth();
  const blanks = firstDay === 0 ? 6 : firstDay - 1; // Lundi en premier

  // Plats de tous les menus disponibles pour le service
  const platsDisponibles = menus
    .filter(m => m.service === service || m.service === 'tous')
    .flatMap(m => (m.plats || []).map(p => ({ ...p, menu_nom: m.nom })));
  const hasCarte = platsDisponibles.length > 0;

  const canNext = etape === 1 ? (dateChoisie && heure && form.nom) : true;

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <UtensilsCrossed size={22} color="#1a56db" />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{restaurant.nom}</div>
            {restaurant.description && <div style={{ fontSize: 12, color: '#6b7280' }}>{restaurant.description}</div>}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 16px' }}>

        {/* Infos contact */}
        {(restaurant.adresse || restaurant.telephone || restaurant.email) && (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {restaurant.adresse && <span style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}><MapPin size={13} color="#9ca3af" />{restaurant.adresse}</span>}
            {restaurant.telephone && <a href={`tel:${restaurant.telephone}`} style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}><Phone size={13} color="#9ca3af" />{restaurant.telephone}</a>}
            {restaurant.email && <a href={`mailto:${restaurant.email}`} style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={13} color="#9ca3af" />{restaurant.email}</a>}
          </div>
        )}

        {/* Progression */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[{ n: 1, label: 'Ma réservation' }, ...(hasCarte ? [{ n: 2, label: 'Pré-commander' }] : []), { n: hasCarte ? 3 : 2, label: 'Confirmation' }].map((s, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ width: '100%', height: 4, background: etape >= s.n ? '#1a56db' : '#e5e7eb', borderRadius: 4, marginBottom: 6 }} />
              <span style={{ fontSize: 11, color: etape >= s.n ? '#1a56db' : '#9ca3af', fontWeight: etape === s.n ? 600 : 400 }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── ÉTAPE 1 : Infos réservation ── */}
        {etape === 1 && (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, padding: 24 }}>

            {/* Service */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Service</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ v: 'midi', l: 'Déjeuner (midi)' }, { v: 'soir', l: 'Dîner (soir)' }].map(sv => (
                  <button key={sv.v} onClick={() => { setService(sv.v); setHeure(''); }}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `2px solid ${service === sv.v ? '#1a56db' : '#e5e7eb'}`, background: service === sv.v ? '#eff6ff' : 'white', color: service === sv.v ? '#1a56db' : '#374151', fontWeight: service === sv.v ? 600 : 400, fontSize: 14, cursor: 'pointer' }}>
                    {sv.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendrier */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <button onClick={() => navMois(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#374151' }}><ChevronLeft size={18} /></button>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{MOIS_FR[mois]} {annee}</span>
                <button onClick={() => navMois(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#374151' }}><ChevronRight size={18} /></button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {JOURS_FR.slice(1).concat(JOURS_FR[0]).map(j => (
                  <div key={j} style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', fontWeight: 600, padding: '4px 0' }}>{j}</div>
                ))}
                {Array.from({ length: blanks }).map((_, i) => <div key={`b${i}`} />)}
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(jour => {
                  const passe = isPassee(jour);
                  const sel = dateChoisie === jour;
                  return (
                    <button key={jour} onClick={() => !passe && setDateChoisie(jour)} disabled={passe}
                      style={{ aspectRatio: '1', borderRadius: 8, border: sel ? '2px solid #1a56db' : '1px solid transparent', background: sel ? '#1a56db' : passe ? '#f9fafb' : 'white', color: sel ? 'white' : passe ? '#d1d5db' : '#111827', fontSize: 13, fontWeight: sel ? 700 : 400, cursor: passe ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {jour}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Heure */}
            {dateChoisie && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  <Clock size={13} style={{ marginRight: 6 }} />Heure
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {heures.map(h => (
                    <button key={h} onClick={() => setHeure(h)}
                      style={{ padding: '8px 16px', borderRadius: 8, border: `2px solid ${heure === h ? '#1a56db' : '#e5e7eb'}`, background: heure === h ? '#1a56db' : 'white', color: heure === h ? 'white' : '#374151', fontSize: 13, fontWeight: heure === h ? 600 : 400, cursor: 'pointer' }}>
                      {h}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Couverts */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                <Users size={13} style={{ marginRight: 6 }} />Nombre de couverts
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <button onClick={() => setCouverts(c => Math.max(1, c - 1))} style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Minus size={16} /></button>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#111827', minWidth: 32, textAlign: 'center' }}>{couverts}</span>
                <button onClick={() => setCouverts(c => Math.min(20, c + 1))} style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={16} /></button>
              </div>
            </div>

            {/* Coordonnées */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Vos coordonnées</div>
              <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Nom complet *" required
                style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 14, outline: 'none' }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="E-mail" type="email"
                  style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 14, outline: 'none' }} />
                <input value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} placeholder="Téléphone"
                  style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 14, outline: 'none' }} />
              </div>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Allergie, occasion spéciale, demande particulière…"
                style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 14, outline: 'none' }} />
            </div>

            <button onClick={() => hasCarte ? setEtape(2) : setEtape(3)} disabled={!canNext}
              style={{ width: '100%', padding: '14px 0', background: canNext ? '#1a56db' : '#e5e7eb', color: canNext ? 'white' : '#9ca3af', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: canNext ? 'pointer' : 'default' }}>
              {hasCarte ? 'Choisir mes plats →' : 'Confirmer ma réservation →'}
            </button>
            {!canNext && <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 8 }}>Choisissez une date, une heure et renseignez votre nom</p>}
          </div>
        )}

        {/* ── ÉTAPE 2 : Pré-commande ── */}
        {etape === 2 && (
          <div>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, padding: 24, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Pré-commandez votre repas</div>
              <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                Facultatif mais apprécié — aidez notre équipe à préparer votre venue. Vous pouvez toujours commander sur place.
              </p>
            </div>

            {Object.keys(CATEGORIES_FR).map(cat => {
              const plats = platsDisponibles.filter(p => p.categorie === cat);
              if (!plats.length) return null;
              return (
                <div key={cat} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: '16px 20px', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>{CATEGORIES_FR[cat]}</div>
                  {plats.map(plat => (
                    <div key={plat.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>{plat.nom}</div>
                        {plat.description && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{plat.description}</div>}
                      </div>
                      {plat.prix && <span style={{ fontSize: 13, fontWeight: 600, color: '#1a56db' }}>{parseFloat(plat.prix).toFixed(2)} €</span>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {commandes[plat.id] ? (
                          <>
                            <button onClick={() => changeCmd(plat.id, -1)} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Minus size={13} /></button>
                            <span style={{ fontWeight: 700, minWidth: 20, textAlign: 'center', fontSize: 15 }}>{commandes[plat.id]}</span>
                            <button onClick={() => changeCmd(plat.id, 1)} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #1a56db', background: '#1a56db', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={13} color="white" /></button>
                          </>
                        ) : (
                          <button onClick={() => changeCmd(plat.id, 1)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #1a56db', background: 'white', color: '#1a56db', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>+ Ajouter</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={() => setEtape(1)} style={{ flex: 1, padding: '13px 0', background: 'white', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                ← Retour
              </button>
              <button onClick={() => setEtape(3)} style={{ flex: 2, padding: '13px 0', background: '#1a56db', color: 'white', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                {totalPlats > 0 ? `Confirmer avec ${totalPlats} plat(s) →` : 'Confirmer sans pré-commande →'}
              </button>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 3 : Récapitulatif ── */}
        {etape === 3 && (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 20 }}>Récapitulatif</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {[
                { icon: Calendar, label: `${dateChoisie}/${mois + 1}/${annee} à ${heure}` },
                { icon: Users, label: `${couverts} couvert${couverts > 1 ? 's' : ''} — ${service === 'midi' ? 'Déjeuner' : 'Dîner'}` },
                { icon: UtensilsCrossed, label: form.nom + (form.telephone ? ` — ${form.telephone}` : '') },
              ].map((row, i) => {
                const Icon = row.icon;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f9fafb', borderRadius: 10 }}>
                    <Icon size={16} color="#1a56db" />
                    <span style={{ fontSize: 14, color: '#374151' }}>{row.label}</span>
                  </div>
                );
              })}
            </div>

            {totalPlats > 0 && (
              <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 16, marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Pré-commande ({totalPlats} plat{totalPlats > 1 ? 's' : ''})</div>
                {Object.entries(commandes).map(([platId, qte]) => {
                  const plat = platsDisponibles.find(p => p.id === parseInt(platId));
                  if (!plat) return null;
                  return (
                    <div key={platId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', padding: '3px 0' }}>
                      <span>{qte}× {plat.nom}</span>
                      {plat.prix && <span>{(parseFloat(plat.prix) * qte).toFixed(2)} €</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {form.notes && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400e', marginBottom: 20 }}>
                Note : {form.notes}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setEtape(hasCarte ? 2 : 1)} style={{ flex: 1, padding: '13px 0', background: 'white', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                ← Modifier
              </button>
              <button onClick={submit} disabled={envoi}
                style={{ flex: 2, padding: '13px 0', background: envoi ? '#93c5fd' : '#1a56db', color: 'white', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: envoi ? 'default' : 'pointer' }}>
                {envoi ? 'Envoi…' : 'Confirmer ma réservation ✓'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
